import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sparkles, Check, ArrowRight, Dumbbell, HeartPulse, Infinity as Inf } from "lucide-react";
import { PROGRAM_LABELS, Disclaimer, CoachBadge } from "@/components/program/shared";

const ICONS = { weight_loss: HeartPulse, performance: Dumbbell, longevity: Inf };
const BLURB = {
  weight_loss: "Sustainable fat loss through nutrition, training, walking and habit change.",
  performance: "Strength, power, endurance and conditioning built around your sport and schedule.",
  longevity: "Strength preservation, mobility, aerobic fitness, sleep and healthy-aging habits.",
};

export const Unlock = ({ ent, onClaimed, onChange }) => {
  const [plans, setPlans] = useState([]);
  const [dur, setDur] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/programs/config").then(({ data }) => setPlans(data.membership_plans || [])); }, []);

  const available = (ent.accesses || []).filter((a) => a.status === "AVAILABLE");

  const claim = async (a) => {
    setBusy(true);
    try {
      const duration = dur[a.id] || Math.min(a.duration_weeks || 12, 12);
      const { data } = await api.post("/programs/claim", { access_id: a.id, program_type: a.program_type, duration_weeks: duration });
      toast.success("Let's build your program");
      onClaimed(data.access_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start"); }
    setBusy(false);
  };

  const subscribe = async (plan) => {
    setBusy(true);
    try { await api.post("/programs/membership/subscribe", { plan_id: plan.id }); toast.success(`${plan.name} activated`); onChange(); }
    catch { toast.error("Subscription failed"); }
    setBusy(false);
  };

  return (
    <div className="pt-28 pb-24 px-4 sm:px-8 lg:px-12 max-w-6xl mx-auto min-h-screen bg-[#04060b]" data-testid="program-unlock">
      <div className="mono-label mb-3">CG Performance OS</div>
      <h1 className="font-serif text-4xl sm:text-6xl text-white mb-3">My Program</h1>
      <p className="text-slate-400 text-sm max-w-xl mb-6">A premium, adaptive performance program — built for you by the CG Labz Performance Team and adjusted around your real life, week after week.</p>
      <CoachBadge />

      {available.length > 0 ? (
        <>
          <div className="mono-label mt-12 mb-4">You&apos;ve unlocked — choose your program</div>
          <div className="grid sm:grid-cols-3 gap-4" data-testid="unlock-available">
            {available.map((a) => {
              const I = ICONS[a.program_type] || Sparkles;
              const maxD = a.duration_weeks || 24;
              return (
                <div key={a.id} className="glass rounded-2xl p-6 flex flex-col" data-testid={`unlock-${a.program_type}`}>
                  <I size={26} className="text-cyan-300" />
                  <div className="serif-display text-2xl text-white mt-4">{PROGRAM_LABELS[a.program_type]}</div>
                  <p className="text-[13px] text-slate-400 mt-2 flex-1">{BLURB[a.program_type]}</p>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-4 mb-2">Duration</div>
                  <div className="flex gap-2">
                    {[12, 24].filter((d) => d <= maxD).map((d) => (
                      <button key={d} onClick={() => setDur((s) => ({ ...s, [a.id]: d }))} data-testid={`unlock-dur-${a.program_type}-${d}`}
                        className={`px-4 py-2 rounded-lg text-xs font-mono border ${((dur[a.id] || Math.min(maxD, 12)) === d) ? "border-cyan-400/70 bg-cyan-400/10 text-cyan-300" : "border-white/10 text-slate-400"}`}>
                        {d} weeks
                      </button>
                    ))}
                  </div>
                  <Button disabled={busy} onClick={() => claim(a)} className="btn-primary rounded-lg mt-5" data-testid={`unlock-start-${a.program_type}`}>
                    Start <ArrowRight size={14} className="ml-2" />
                  </Button>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <>
          <div className="mono-label mt-12 mb-4">Unlock a program with a membership</div>
          <div className="grid sm:grid-cols-2 gap-4" data-testid="membership-plans">
            {plans.map((p) => (
              <div key={p.id} className="glass rounded-2xl p-6" data-testid={`plan-${p.slug}`}>
                <div className="flex items-baseline justify-between">
                  <div className="serif-display text-2xl text-white">{p.name}</div>
                  <div className="font-mono text-cyan-300">${p.price}<span className="text-slate-500 text-xs">/{p.interval}</span></div>
                </div>
                <ul className="mt-4 space-y-2">
                  {(p.benefits || []).map((b) => (
                    <li key={b} className="flex items-center gap-2 text-[13px] text-slate-300"><Check size={13} className="text-cyan-400" /> {b}</li>
                  ))}
                </ul>
                <Button disabled={busy} onClick={() => subscribe(p)} className="btn-primary rounded-lg w-full mt-5" data-testid={`subscribe-${p.slug}`}>
                  Activate {p.name}
                </Button>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-4">You can also unlock a program with a qualifying bulk order, or ask our team for access.</p>
        </>
      )}
      <Disclaimer className="mt-12" />
    </div>
  );
};
